import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';
import { authedWrite } from './apiClient';

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export type DepositFileKind = 'receipt' | 'envelope';

export interface DepositReconFile {
  id: number;
  url: string | null;
  name: string | null;
  kind?: DepositFileKind;
}

export async function uploadDepositReconFile(
  reconId: number,
  kind: DepositFileKind,
  file: File,
): Promise<DepositReconFile> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }

  const token = authToken.value;
  const res = await fetch(
    `${API_BASE}${withViewAs(`/api/v1/money/deposit-recons/${reconId}/files`)}`,
    {
      method: 'POST',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
        'x-file-kind': kind,
        'idempotency-key': crypto.randomUUID(),
      },
      body: file,
    },
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  return (body as { file: DepositReconFile }).file;
}

export async function deleteDepositReconFile(fileId: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/money/deposit-recon-files/${fileId}`);
}

export async function uploadDepositBatchFile(
  batchId: number,
  kind: DepositFileKind,
  file: File,
): Promise<DepositReconFile> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }

  const token = authToken.value;
  const res = await fetch(
    `${API_BASE}${withViewAs(`/api/v1/money/deposit-batches/${batchId}/files`)}`,
    {
      method: 'POST',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
        'x-file-kind': kind,
        'idempotency-key': crypto.randomUUID(),
      },
      body: file,
    },
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  return (body as { file: DepositReconFile }).file;
}

export async function deleteDepositBatchFile(fileId: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/money/deposit-batch-files/${fileId}`);
}